import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Alert,
  Box,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { pinService } from '@/services/pinService';
import { authService } from '@/services/authService';

interface PinVerifyDialogProps {
  open: boolean;
  /** Judul/alasan singkat (mis. "Tarik Saldo"). */
  reason?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const PinInput = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    '& input': {
      textAlign: 'center',
      fontSize: '1.3rem',
      fontWeight: 700,
      letterSpacing: '0.3em',
      fontFamily: 'monospace',
    },
  },
}));

const fmtCountdown = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const PinVerifyDialog: React.FC<PinVerifyDialogProps> = ({ open, reason, onSuccess, onCancel }) => {
  const [mode, setMode] = useState<'pin' | 'password'>('pin');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [now, setNow] = useState<number>(() => 0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset state tiap kali dibuka.
  useEffect(() => {
    if (open) {
      setMode('pin');
      setPin('');
      setPassword('');
      setError('');
      setInfo('');
      setBusy(false);
      setLockedUntil(null);
    }
  }, [open]);

  // Tick untuk countdown lockout.
  useEffect(() => {
    if (!lockedUntil) {
      if (tickRef.current) clearInterval(tickRef.current);
      return undefined;
    }
    setNow(Date.now());
    tickRef.current = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [lockedUntil]);

  const lockRemaining = lockedUntil ? lockedUntil.getTime() - now : 0;
  const isLocked = !!lockedUntil && lockRemaining > 0;

  const handleVerifyPin = async () => {
    if (pin.length !== 6) return;
    setBusy(true);
    setError('');
    const { data, error: rpcErr } = await pinService.verifyPin(pin);
    setBusy(false);

    if (rpcErr || !data) {
      setError(rpcErr || 'Gagal memverifikasi PIN.');
      return;
    }
    if (data.status === 'ok' || data.status === 'no_pin') {
      onSuccess();
      return;
    }
    if (data.status === 'locked') {
      setLockedUntil(data.lockedUntil ?? new Date(Date.now() + 10 * 60 * 1000));
      setPin('');
      setError('Terlalu banyak percobaan. PIN terkunci sementara.');
      return;
    }
    // wrong
    setPin('');
    setError(`PIN salah. Sisa ${data.attemptsLeft ?? 0} percobaan sebelum terkunci.`);
  };

  const handleVerifyPassword = async () => {
    if (!password) return;
    setBusy(true);
    setError('');
    const ok = await authService.verifyPassword(password);
    if (ok) {
      await pinService.resetPinLock();
      setBusy(false);
      onSuccess();
      return;
    }
    setBusy(false);
    setError('Password salah.');
  };

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <LockOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
          {mode === 'pin' ? 'Masukkan PIN' : 'Verifikasi Password'}
        </Typography>
        {reason && (
          <Typography variant="caption" color="text.secondary">
            untuk {reason}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {busy && <LinearProgress sx={{ mb: 2 }} />}

        {mode === 'pin' ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isLocked ? (
              <Alert severity="warning">
                PIN terkunci. Coba lagi dalam <strong>{fmtCountdown(lockRemaining)}</strong>, atau
                gunakan password.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Masukkan 6 digit PIN transaksi Anda.
              </Typography>
            )}

            <PinInput
              autoFocus
              fullWidth
              type="password"
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPin(); }}
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              disabled={busy || isLocked}
              helperText={`${pin.length}/6 digit`}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button variant="text" size="small" onClick={() => { setMode('password'); setError(''); }}>
              Lupa PIN? Gunakan password
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Masukkan password akun Anda untuk melanjutkan.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPassword(); }}
              disabled={busy}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {info && <Alert severity="info">{info}</Alert>}
            <Button variant="text" size="small" onClick={() => { setMode('pin'); setError(''); }}>
              Kembali ke PIN
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy}>
          Batal
        </Button>
        {mode === 'pin' ? (
          <Button variant="contained" onClick={handleVerifyPin} disabled={busy || isLocked || pin.length !== 6}>
            Verifikasi
          </Button>
        ) : (
          <Button variant="contained" onClick={handleVerifyPassword} disabled={busy || !password}>
            Verifikasi
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PinVerifyDialog;
